import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/contexts/AuthContext";

export default function PaymentSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling");
    const [tries, setTries] = useState(0);
    const { refresh } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let cancelled = false;

        const poll = async (attempt = 0) => {
            if (cancelled) return;
            if (attempt >= 10) {
                setStatus("timeout");
                return;
            }
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                setTries(attempt + 1);
                if (data.payment_status === "paid") {
                    trackEvent("purchase", { transaction_id: sessionId, value: (data.amount || 0) / 100, currency: "EUR" });
                    await refresh();
                    setStatus("paid");
                    return;
                }
                if (data.payment_status === "processing") {
                    setStatus("processing");
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                setTimeout(() => poll(attempt + 1), 2000);
            } catch {
                setStatus("error");
            }
        };
        poll(0);
        return () => { cancelled = true; };
    }, [sessionId, refresh]);

    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
            <div className="card-elev max-w-md w-full p-10 text-center">
                {status === "polling" && (
                    <>
                        <Loader2 className="mx-auto animate-spin text-red-500" size={48} />
                        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-6">Zahlung wird verarbeitet...</h1>
                        <p className="text-slate-600 mt-2 text-sm">Versuch {tries} / 10 — bitte warten.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle2 className="mx-auto text-emerald-500" size={64} />
                        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-6">Zahlung erfolgreich!</h1>
                        <p className="text-slate-600 mt-2">Dein Plan wurde aktiviert. Viel Erfolg mit deinen Bewerbungen!</p>
                        <Link to="/dashboard" className="mt-8 inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold btn-primary-grad" data-testid="payment-success-cta">
                            Zum Dashboard <ArrowRight size={18} />
                        </Link>
                    </>
                )}
                {status === "processing" && (
                    <>
                        <Loader2 className="mx-auto text-amber-500" size={64} />
                        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-6">Zahlung unterwegs 🏦</h1>
                        <p className="text-slate-600 mt-2 text-sm">Deine Bank-Zahlung (z. B. SEPA-Lastschrift) wird verarbeitet — das dauert in der Regel 2–5 Werktage. Sobald das Geld eingegangen ist, wird dein Kauf automatisch freigeschaltet und du bekommst Bescheid.</p>
                        <Link to="/dashboard" className="mt-8 inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold btn-primary-grad" data-testid="payment-processing-cta">
                            Zum Dashboard <ArrowRight size={18} />
                        </Link>
                    </>
                )}
                {(status === "expired" || status === "timeout" || status === "error") && (
                    <>
                        <XCircle className="mx-auto text-red-500" size={64} />
                        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-6">Etwas ist schiefgelaufen</h1>
                        <p className="text-slate-600 mt-2">{status === "expired" ? "Die Sitzung ist abgelaufen." : "Wir konnten den Status nicht ermitteln."}</p>
                        <Link to="/preise" className="mt-8 inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold bg-slate-900 text-white">
                            Erneut versuchen
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
